import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { AppSettings } from './../../config/app-settings.js';
import Config from '../config.json';

function PagesLogin() {
	const context = useContext(AppSettings);
	const inputRef = useRef();
	const [buttonText, setButtonText] = useState('Sign In');
	const [errorMessage, setErrorMessage] = useState('');
	const [submitDisabled, setSubmitDisabled] = useState(false);
	const [login, setLogin] = useState({
        'email': '',
        'password': ''
    });

    // New state for MFA Modal
    const [showMfaModal, setShowMfaModal] = useState(false);
    const [mfaCode, setMfaCode] = useState('');
    const [mfaErrorMessage, setMfaErrorMessage] = useState('');
    const [pendingAuthData, setPendingAuthData] = useState(null);
    const [mfaVerifying, setMfaVerifying] = useState(false);
    const [mfaButtonText, setMfaButtonText] = useState('Verify Code');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setLogin({ ...login, [name]: value });
        setErrorMessage('');
    };

    const handleMfaCodeChange = (e) => {
        setMfaCode(e.target.value);
        setMfaErrorMessage('');
    };

    const resetLoginForm = () => {
        setButtonText('Sign In');
        setSubmitDisabled(false);
        setPendingAuthData(null);
        setShowMfaModal(false);
        setMfaCode('');
        setMfaErrorMessage('');
        setLogin({ email: login.email, password: '' }); // Keep email, clear password
    };
	
	const handleSubmit = (event) => {
        event.preventDefault();
		if (!login.email || !login.password) {
			setErrorMessage('Please provide both email and password.');
			return;
		}
        setButtonText('Signing In...');
		setSubmitDisabled(true);
        setErrorMessage('');

        fetch('https://quefg0iqb8.execute-api.us-east-2.amazonaws.com/systemauthenticate', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': Config.systemauthenticate,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                'user_id': login.email,
                'password': login.password
            })
        })
        .then((response) => response.json())
        .then((authResponse) => {
            if (authResponse.token && authResponse.token.length === parseInt(process.env.REACT_APP_TOKEN_LENGTH)) {
                setButtonText('Verifying Identity...');
                setPendingAuthData(authResponse); // Store auth data temporarily

                const getBaseURL = window.location.hostname === '192.168.1.45'
                    ? 'http://localhost:8383'
                    : 'https://api.galeforceai.com';

                return fetch(`${getBaseURL}/system/initiatemfa`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': Config.mfainitiate,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        'email': login.email,
                        'token': authResponse.token
                    })
                })
                .then(mfaResponse => mfaResponse.json())
                .then(mfaData => {
                    if (mfaData.success) {
                        console.log('MFA initiated:', mfaData);
                        setButtonText('MFA Required'); // Update main button text
                        setShowMfaModal(true); // Show MFA modal instead of redirecting
                        setSubmitDisabled(true); // Keep main form submit button disabled
                    } else {
                        console.error('MFA initiation failed. API response:', mfaData);
                        setErrorMessage(mfaData.error || 'Failed to initiate 2FA. Please try again.');
                        resetLoginForm();
                        setLogin({ ...login, password: ''}); // Clear password on MFA init fail
                    }
                })
                .catch(mfaError => {
                    console.error('MFA Initiation Error:', mfaError);
                    setErrorMessage('An error occurred during 2FA setup. Please try again.');
                    resetLoginForm();
                    setLogin({ ...login, password: ''});
                });
            } else {
                localStorage.removeItem('user_id');
                localStorage.removeItem('optimus');
                localStorage.removeItem('first_name');
                localStorage.removeItem('last_name');
                localStorage.removeItem('team_id');
                setErrorMessage(authResponse.error || 'Invalid username or password');
                resetLoginForm();
                setLogin({ ...login, password: ''});
            }
        })
        .catch(function (err) {
            console.log('Authentication Error:', err);
            setErrorMessage('An authentication error occurred. Please try again.');
            resetLoginForm();
            setLogin({ ...login, password: ''});
        });
    };

    const handleMfaVerification = (event) => {
        event.preventDefault();
        if (!mfaCode) {
            setMfaErrorMessage('Please enter your MFA code.');
            return;
        }
        setMfaVerifying(true);
        setMfaButtonText('Verifying...');
        setMfaErrorMessage('');

        const getBaseURL = window.location.hostname === '192.168.1.45'
            ? 'http://localhost:8383'
            : 'https://api.galeforceai.com';

        fetch(`${getBaseURL}/system/verifymfa`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': Config.mfaverify,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                'email': login.email,
                'token': pendingAuthData.token, // Token from initial auth
                'mfa_code': mfaCode
            })
        })
        .then(response => response.json())
        .then(verificationResponse => {
            if (verificationResponse.success) {
                localStorage.setItem('user_id', pendingAuthData.user_id);
                localStorage.setItem('optimus', pendingAuthData.token);
                localStorage.setItem('first_name', pendingAuthData.first_name);
                localStorage.setItem('last_name', pendingAuthData.last_name);
                localStorage.setItem('team_id', pendingAuthData.team_id);

                setShowMfaModal(false);
                setMfaCode('');
                setPendingAuthData(null);
                setButtonText('Signed In');
                setSubmitDisabled(true);
                window.location.href = '/';
            } else {
                setMfaErrorMessage(verificationResponse.error || 'Invalid MFA code. Please try again.');
                setMfaCode('');
            }
        })
        .catch(error => {
            console.error('MFA Verification Error:', error);
            setMfaErrorMessage('An error occurred during MFA verification. Please try again.');
        })
        .finally(() => {
            setMfaVerifying(false);
            setMfaButtonText('Verify Code');
        });
    };

    const handleCancelMfa = () => {
        setShowMfaModal(false);
        setMfaCode('');
        setMfaErrorMessage('');
        setPendingAuthData(null);
        // Reset main form to allow re-login or modification
        setButtonText('Sign In');
        setSubmitDisabled(false);
        setErrorMessage('MFA verification cancelled.');
        // Optionally focus password or email inputRef.current.focus();
    };

	useEffect(() => {
		context.setAppHeaderNone(true);
		context.setAppSidebarNone(true);
		context.setAppContentClass('p-0');

		if (inputRef.current) {
			inputRef.current.focus();
		}
		
		return function cleanUp() {
			context.setAppHeaderNone(false);
			context.setAppSidebarNone(false);
			context.setAppContentClass('');
		};
		
		// eslint-disable-next-line
	}, []);

	return (
		<div className="login">
			<div className="login-content">
				<form onSubmit={handleSubmit}>
                    <h1 className="page-header gradient-text text-center">
                        <span className="gradient-text text-center">Sign In To<br />Gale Force AI</span>
                    </h1>

					<div className="text-inverse text-opacity-50 text-center mb-4">
                        {errorMessage ?
                            <div className="text-danger"><b>{errorMessage}</b></div>
                            : <div>For your protection, please verify your identity.</div>
                        }
					</div>
					<div className="mb-3">
						<label className="form-label">Email Address <span className="text-danger">*</span></label>
						<input type="text" id="email" name="email" value={login.email} onChange={handleChange} className="form-control form-control-lg bg-white bg-opacity-5" placeholder="" ref={inputRef} disabled={submitDisabled || showMfaModal} />
					</div>
					<div className="mb-3">
						<div className="d-flex">
							<label className="form-label">Password <span className="text-danger">*</span></label>
							{/* <a href="#/" className="ms-auto text-inverse text-decoration-none text-opacity-50">Forgot password?</a> */}
						</div>
						<input type="password" id="password" name="password" value={login.password} onChange={handleChange} className="form-control form-control-lg bg-white bg-opacity-5" placeholder="" disabled={submitDisabled || showMfaModal} />
					</div>
					<button type="submit" className="btn btn-outline-theme btn-lg d-block w-100 fw-500 mb-3" disabled={submitDisabled || showMfaModal}>{buttonText}</button>
					{/* <div className="text-center text-inverse text-opacity-50" style={{ display: 'none' }}>
						Don't have an account yet? <Link to="/signup">Sign up</Link>.
					</div> */}
				</form>
			</div>

            {/* MFA Modal */}
            {showMfaModal && (
                <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1050 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Enter MFA Code</h5>
                                <button type="button" className="btn-close" onClick={handleCancelMfa} disabled={mfaVerifying}></button>
                            </div>
                            <form onSubmit={handleMfaVerification}>
                                <div className="modal-body">
                                    {mfaErrorMessage && <div className="alert alert-danger">{mfaErrorMessage}</div>}
                                    <p>A code has been sent to your {login.email.split('@')[1]} email account.</p>
                                    <div className="mb-3">
                                        <label htmlFor="mfaCode" className="form-label">MFA Code</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="mfaCode"
                                            value={mfaCode}
                                            onChange={handleMfaCodeChange}
                                            placeholder="Enter code"
                                            autoFocus
                                            disabled={mfaVerifying}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={handleCancelMfa} disabled={mfaVerifying}>Cancel</button>
                                    <button type="submit" className="btn btn-primary" disabled={mfaVerifying || !mfaCode}>
                                        {mfaButtonText}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
		</div>
	)
}

export default PagesLogin;